import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

import { type CaregiverShiftIds } from '@apps/web/lib/caregiver_shift_selections';

export type OutreachCaregiverSelection = {
    readonly outreachId: string;
    readonly caregiverShiftIds: CaregiverShiftIds;
};

type OutreachCaregiverSelectionContextValue = {
    readonly selection: OutreachCaregiverSelection | null;
    readonly setSelection: (
        selection: OutreachCaregiverSelection | null,
    ) => void;
};

const OutreachCaregiverSelectionContext =
    createContext<OutreachCaregiverSelectionContextValue>({
        selection: null,
        setSelection: () => {},
    });

export function OutreachCaregiverSelectionProvider({
    children,
}: {
    readonly children: ReactNode;
}) {
    const [selection, setSelectionState] =
        useState<OutreachCaregiverSelection | null>(null);
    const setSelection = useCallback(
        (nextSelection: OutreachCaregiverSelection | null) => {
            setSelectionState(nextSelection);
        },
        [],
    );
    const value = useMemo(
        () => ({ selection, setSelection }),
        [selection, setSelection],
    );

    return (
        <OutreachCaregiverSelectionContext.Provider value={value}>
            {children}
        </OutreachCaregiverSelectionContext.Provider>
    );
}

export function useOutreachCaregiverSelection() {
    return useContext(OutreachCaregiverSelectionContext);
}
